import React from 'react';
import { PiTrashDuotone } from 'react-icons/pi';
import { Button, Input } from 'rizzui';
import CustomSelect from '../../../components/forms/CustomSelect';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import { FilterOptions } from '../../../hooks/useFilters';
import { RootState } from '../../../redux/store';
import { useSelector } from 'react-redux';
import useCompanies from '../../../hooks/api-calls/useCompanies';
import { speciesGroupSelector } from '../../../utils/SelectorMapping';

interface SpeciesFiltersProps {
    updateFilters: (filters: any) => void
    resetFilters: (limit: any) => void
    filters: FilterOptions
}

const SpeciesFilters: React.FC<SpeciesFiltersProps> = ({ filters, updateFilters, resetFilters }) => {

    // HOOKS

    const { userCan } = usePrivilege();
    const { getCompaniesList } = useCompanies();
    const { user } = useSelector((state: RootState) => state.auth);
    const isSuperAdmin = user?.roles.includes('Superadministrador');

    // RENDER

    return (
        <>
           <Input
                id="commonName"
                label="Nombre Común"
                placeholder="Buscar por nombre común"
                value={filters.filter_filters?.commonName ?? ''}
                onChange={(e: any) => updateFilters({ commonName: e.target.value })}
            />

            <Input
                id="scientificName"
                label="Nombre Científico"
                placeholder="Buscar por nombre científico"
                value={filters.filter_filters?.scientificName ?? ''}
                onChange={(e: any) => updateFilters({ scientificName: e.target.value })}
                inputClassName='italic'
            />

            <Input
                id="acronym"
                label="Acrónimo"
                placeholder="Buscar por acrónimo"
                value={filters.filter_filters?.acronym ?? ''}
                onChange={(e: any) => updateFilters({ acronym: e.target.value })}
            />

            <CustomSelect
                isSearchable
                id={'family'}
                label="Grupo"
                placeholder='grupo'
                options={speciesGroupSelector}
                onChange={(e: any) => {
                    if (e.value === 'null') {
                        updateFilters({ family: null });
                        return;
                    }
                    updateFilters({ family: e.value });
                }}
                value={speciesGroupSelector.find((option: any) => option.value === filters.filter_filters?.family)}
            />

            <Input
                id="specieIdentifier"
                label="Identificador de Especie"
                placeholder="Buscar por identificador de especie"
                value={filters.filter_filters?.specieIdentifier ?? ''}
                onChange={(e: any) => updateFilters({ specieIdentifier: e.target.value })}
            />

            <div className='grid md:grid-cols-2 grid-cols-1 gap-4'>

            {/* CENSUS FILTER */}

            <CustomSelect
                isSearchable
                id={'censusAvailable'}
                label="Censo disponible"
                options={[
                    { label: 'Todos', value: 'null' },
                    { label: 'Sí', value: true },
                    { label: 'No', value: false }
                ]}
                onChange={(e: any) => {
                    if (e.value === 'null') {
                        updateFilters({ censusAvailable: null });
                        return;
                    }
                    updateFilters({ censusAvailable: e.value });
                }}
                value={filters.filter_filters?.censusAvailable !== null && filters.filter_filters?.censusAvailable !== undefined
                    ? { label: filters.filter_filters?.censusAvailable ? 'Sí' : 'No', value: filters.filter_filters?.censusAvailable }
                    : { label: 'Todos', value: 'null' }}
            />

            {/* OFC FILTER */}

            <CustomSelect
                isSearchable
                id={'ofcAvailable'}
                label="Observación disponible"
                options={[
                    { label: 'Todos', value: 'null' },
                    { label: 'Sí', value: true },
                    { label: 'No', value: false }
                ]}
                onChange={(e: any) => {
                    if (e.value === 'null') {
                        updateFilters({ ofcAvailable: null });
                        return;
                    }
                    updateFilters({ ofcAvailable: e.value });
                }}
                value={filters.filter_filters?.ofcAvailable !== null && filters.filter_filters?.ofcAvailable !== undefined
                    ? { label: filters.filter_filters?.ofcAvailable ? 'Sí' : 'No', value: filters.filter_filters?.ofcAvailable }
                    : { label: 'Todos', value: 'null' }}
            />

            {/* CARRION FILTER */}

            <CustomSelect
                isSearchable
                id={'carrionAvailable'}
                label="Carroña disponible"
                options={[
                    { label: 'Todos', value: 'null' },
                    { label: 'Sí', value: true },
                    { label: 'No', value: false }
                ]}
                onChange={(e: any) => {
                    if (e.value === 'null') {
                        updateFilters({ carrionAvailable: null });
                        return;
                    }
                    updateFilters({ carrionAvailable: e.value });
                }}
                value={filters.filter_filters?.carrionAvailable !== null && filters.filter_filters?.carrionAvailable !== undefined
                    ? { label: filters.filter_filters?.carrionAvailable ? 'Sí' : 'No', value: filters.filter_filters?.carrionAvailable }
                    : { label: 'Todos', value: 'null' }}
            />

            {/* ISSUES FILTER */}

            <CustomSelect
                isSearchable
                id={'issuesAvailable'}
                label="Incidencias disponibles"
                options={[
                    { label: 'Todos', value: 'null' },
                    { label: 'Sí', value: true },
                    { label: 'No', value: false }
                ]}
                onChange={(e: any) => {
                    if (e.value === 'null') {
                        updateFilters({ issuesAvailable: null });
                        return;
                    }
                    updateFilters({ issuesAvailable: e.value });
                }}
                value={filters.filter_filters?.issuesAvailable !== null && filters.filter_filters?.issuesAvailable !== undefined
                    ? { label: filters.filter_filters?.issuesAvailable ? 'Sí' : 'No', value: filters.filter_filters?.issuesAvailable }
                    : { label: 'Todos', value: 'null' }}
            />

            {/* CAT ANDALUSIA FILTER */}

            <CustomSelect
                isSearchable
                id={'catAndalucia'}
                label="Protegida en Andalucía"
                options={[
                    { label: 'Todos', value: 'null' },
                    { label: 'Sí', value: true },
                    { label: 'No', value: false }
                ]}
                onChange={(e: any) => {
                    if (e.value === 'null') {
                        updateFilters({ catAndalucia: null });
                        return;
                    }
                    updateFilters({ catAndalucia: e.value });
                }}
                value={filters.filter_filters?.catAndalucia !== null && filters.filter_filters?.catAndalucia !== undefined
                    ? { label: filters.filter_filters?.catAndalucia ? 'Sí' : 'No', value: filters.filter_filters?.catAndalucia }
                    : { label: 'Todos', value: 'null' }}
            />

            {/* CAT NACIONAL FILTER */}

            <CustomSelect
                isSearchable
                id={'catNacional'}
                label="Protegida a nivel nacional"
                options={[
                    { label: 'Todos', value: 'null' },
                    { label: 'Sí', value: true },
                    { label: 'No', value: false }
                ]}
                onChange={(e: any) => {
                    if (e.value === 'null') {
                        updateFilters({ catNacional: null });
                        return;
                    }
                    updateFilters({ catNacional: e.value });
                }}
                value={filters.filter_filters?.catNacional !== null && filters.filter_filters?.catNacional !== undefined
                    ? { label: filters.filter_filters?.catNacional ? 'Sí' : 'No', value: filters.filter_filters?.catNacional }
                    : { label: 'Todos', value: 'null' }}
            />

            </div>

            {isSuperAdmin && (
                <CustomSelect
                    isSearchable
                    id={'company'}
                    label="Compañia"
                    options={getCompaniesList()}
                    onChange={(e: any) => { 
                        updateFilters({ company: e.value})
                    }}
                    value={
                        filters.filter_filters?.company
                            ? getCompaniesList().filter((company: any) => filters.filter_filters?.company.includes(company.value))
                            : null
                           
                    }
                    display={userCan('list', 'companies')}
                />
            )}

            <Button
                size="sm"
                onClick={() => {
                    resetFilters(50);
                }}
                variant="flat"
                className="h-9 bg-gray-200/70"
            >
                <PiTrashDuotone className="me-1.5 h-[17px] w-[17px]" /> Limpiar
            </Button>
        </>
    );
};

export default SpeciesFilters;